import { ComponentProps, ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';

import { ChevronLeftIcon, CloseIcon, LogOutIcon, MoreVerticalIcon, UserIcon } from '@/assets/icons';
import { ChirpeeImage, NoAvatarImage } from '@/assets/images';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  IconButton,
  Separator,
  Skeleton,
  Tag,
} from '@/components';
import {
  Dropdown,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuSimpleItem,
  DropdownMenuTrigger,
  GroupItem,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SlidebarWrapper,
} from '@/components/organisms';
import { useSidebar } from '@/components/organisms/Sidebar/sidebar.constants';
import { RoutesEnum } from '@/enums';
import { useBreakpoints } from '@/hooks/useBreakpoints';
import { useAppNavigate } from '@/router/useAppNavigate';
import { cn } from '@/utils/tailwind';

export type SidebarUser = {
  name: string;
  jobTitle?: string;
  image?: string;
  label?: string;
};

type DashboardSidebarProps = {
  groups: GroupItem[];
  user: SidebarUser;
  header?: ReactNode | string;
  onLogout?: () => void;
  sidebarProps?: Omit<ComponentProps<typeof Sidebar>, 'children'>;
  isNavigationLoading?: boolean;
  isUserProfileLoading?: boolean;
};

const NAV_SKELETON_ROW_COUNT = 8;

const DashboardSidebarContent = ({
  groups,
  user,
  header,
  onLogout,
  sidebarProps,
  isNavigationLoading = false,
  isUserProfileLoading = false,
}: DashboardSidebarProps) => {
  const { t } = useTranslation('components');
  const { open, toggleSidebar } = useSidebar();
  const location = useLocation();
  const navigate = useAppNavigate();

  const { isMobile } = useBreakpoints();

  const [isOpenDropdownMenu, setIsOpenDropdownMenu] = useState(false);

  const handleLogout = () => {
    setIsOpenDropdownMenu(false);
    onLogout?.();
  };

  const handleNavigateAccountSettings = () => {
    setIsOpenDropdownMenu(false);
    navigate(RoutesEnum.ACCOUNT_SETTINGS);
  };

  const isUrlActive = (itemUrl: string) => {
    const currentPath = location.pathname;

    return itemUrl === '/' ? currentPath === '/' : currentPath.includes(itemUrl);
  };

  const renderHeader = () => {
    return isMobile ? (
      <div className="flex items-center w-full">
        <IconButton
          onClick={toggleSidebar}
          variant="teritary"
          icon={<CloseIcon width={20} height={20} />}
          size="sm"
          className="p-1"
          data-testid="close-sidebar-mobile-button"
          aria-label="Toggle sidebar button"
        />
        <div className="mx-auto">{header}</div>
      </div>
    ) : (
      <>
        <div className="visible group-data-[collapsible=icon]:hidden flex min-h-10 min-w-0 flex-1 items-center">
          {header}
        </div>
        <IconButton
          onClick={toggleSidebar}
          variant="teritary"
          icon={
            <ChevronLeftIcon
              width={20}
              height={20}
              className="group-data-[collapsible=icon]:rotate-180 transition-transform"
            />
          }
          size="sm"
          className="shrink-0 p-1"
          data-testid="toggle-sidebar-button"
          aria-label="Toggle sidebar button"
        />
      </>
    );
  };

  const renderSubItems = (item: SidebarMenuItem) => {
    return (
      <div key={item.url}>
        <div className="hidden group-data-[collapsible=icon]:block">
          {item.subitems?.map(subitem => (
            <SidebarMenuItem className="group/submenuitem" key={subitem.url}>
              <SidebarMenuButton
                asChild
                className={cn(
                  '[&>svg]:size-5 h-10 hover:bg-transparent hover:text-custom-warm-grey-700 data-[active=true]:bg-custom-warm-grey-100 data-[active=true]:text-custom-cyan-500 transition-colors',
                  {
                    '!bg-custom-warm-grey-100 text-body-14-semibold': isUrlActive(subitem.url),
                  }
                )}
              >
                <Link className="cursor-pointer" to={subitem.url}>
                  <subitem.icon
                    size={20}
                    className={cn(
                      'text-custom-warm-grey-400 group-hover/submenuitem:text-custom-warm-grey-500 group-data-[active=true]/submenuitem:text-custom-cyan-500 transition-colors',
                      {
                        'text-custom-cyan-500': isUrlActive(subitem.url),
                      }
                    )}
                  />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </div>
        <Accordion
          type="single"
          collapsible
          className="visible group-data-[collapsible=icon]:hidden"
        >
          <AccordionItem value={item.url}>
            <AccordionTrigger className="active:bg-custom-warm-grey-100">
              <SidebarMenuItem className="group/submenuitem">
                <SidebarMenuButton
                  asChild
                  className="[&>svg]:size-5 h-10 hover:bg-transparent hover:text-custom-warm-grey-700 data-[active=true]:bg-custom-warm-grey-100 data-[active=true]:text-custom-cyan-500 transition-colors"
                >
                  <div className="cursor-pointer">
                    <item.icon
                      width={20}
                      height={20}
                      className="text-custom-warm-grey-400 group-hover/submenuitem:text-custom-warm-grey-500 group-data-[active=true]/submenuitem:text-custom-cyan-500 transition-colors"
                    />
                    {item.title}
                  </div>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </AccordionTrigger>
            <AccordionContent>
              <SidebarMenuSub className="border-l-0 ml-[18px]">
                {item.subitems?.map(subitem => (
                  <SidebarMenuSubItem key={subitem.url}>
                    <SidebarMenuSubButton
                      asChild
                      className={cn(
                        'text-custom-warm-grey-500 h-10 hover:bg-transparent hover:text-custom-warm-grey-700 data-[active=true]:bg-custom-warm-grey-100 data-[active=true]:text-custom-cyan-500 transition-colors',
                        {
                          '!bg-custom-warm-grey-100 text-body-14-semibold !text-custom-cyan-500':
                            isUrlActive(subitem.url),
                        }
                      )}
                    >
                      <Link to={subitem.url}>
                        <span>{subitem.title}</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                ))}
              </SidebarMenuSub>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    );
  };

  return (
    <SlidebarWrapper>
      <Sidebar
        {...sidebarProps}
        collapsible="icon"
        className="pt-6"
        data-testid="dashboard-sidebar"
      >
        <SidebarHeader className="mb-10 flex flex-row items-center justify-between gap-2 pl-6 pr-4 py-0 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:gap-0 group-data-[collapsible=icon]:px-0">
          {renderHeader()}
        </SidebarHeader>
        <SidebarContent className="text-custom-warm-grey-500 gap-8">
          {isNavigationLoading ? (
            <div
              className="flex flex-col gap-2 px-4"
              aria-busy="true"
              data-testid="sidebar-nav-skeleton"
            >
              {Array.from({ length: NAV_SKELETON_ROW_COUNT }).map((_, i) => (
                <Skeleton key={i} className="h-10 w-full rounded-md" />
              ))}
            </div>
          ) : (
            groups.map(group => (
              <SidebarGroup
                key={group.id}
                className="py-0 gap-2 group-data-[collapsible=icon]:px-[6px] px-4"
              >
                <Separator className="hidden group-data-[collapsible=icon]:block !w-[30px] mx-auto mb-6" />
                {group.groupLabel && (
                  <SidebarGroupLabel className="h-[17px] text-custom-warm-grey-400">
                    {group.groupLabel}
                  </SidebarGroupLabel>
                )}
                <SidebarGroupContent>
                  <SidebarMenu className="group-data-[collapsible=icon]:gap-2">
                    {group.items.map(item =>
                      item.subitems ? (
                        renderSubItems(item)
                      ) : (
                        <SidebarMenuItem key={item.url} className="group/menuitem">
                          <SidebarMenuButton
                            asChild
                            className={cn(
                              '[&>svg]:size-5 h-10 hover:bg-transparent hover:text-custom-warm-grey-700 data-[active=true]:bg-custom-warm-grey-100 data-[active=true]:text-custom-cyan-500 transition-colors group-data-[collapsible=icon]:size-10!',
                              {
                                'bg-custom-warm-grey-100 hover:bg-custom-warm-grey-100':
                                  !item.externalHref && isUrlActive(item.url),
                              }
                            )}
                          >
                            {item.externalHref ? (
                              <a
                                href={item.externalHref}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="cursor-pointer flex w-full min-w-0 items-center gap-2"
                              >
                                <item.icon
                                  width={20}
                                  height={20}
                                  className="text-custom-warm-grey-400 group-hover/menuitem:text-custom-warm-grey-500 group-data-[active=true]/menuitem:text-custom-cyan-500 transition-colors shrink-0"
                                />
                                <span className="truncate">{item.title}</span>
                              </a>
                            ) : (
                              <Link
                                className="flex w-full min-w-0 cursor-pointer items-center gap-2"
                                to={item.url}
                              >
                                <item.icon
                                  width={20}
                                  height={20}
                                  className={cn(
                                    'text-custom-warm-grey-400 group-hover/menuitem:text-custom-warm-grey-500 group-data-[active=true]/menuitem:text-custom-cyan-500 transition-colors shrink-0',
                                    {
                                      'text-body-14-semibold !text-custom-cyan-500': isUrlActive(
                                        item.url
                                      ),
                                    }
                                  )}
                                />
                                <span
                                  className={cn('truncate', {
                                    'text-body-14-semibold !text-custom-cyan-500': isUrlActive(
                                      item.url
                                    ),
                                  })}
                                >
                                  {item.title}
                                </span>
                              </Link>
                            )}
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      )
                    )}
                  </SidebarMenu>
                </SidebarGroupContent>
              </SidebarGroup>
            ))
          )}
        </SidebarContent>
        <SidebarFooter className="gap-4 px-4 py-3 group-data-[collapsible=icon]:px-2">
          <div className="flex items-center justify-center gap-2">
            <div className="flex items-center gap-2 group-data-[collapsible=icon]:hidden">
              {isUserProfileLoading ? (
                <>
                  <Skeleton
                    className="size-8 shrink-0 rounded-full"
                    data-testid="sidebar-user-skeleton-avatar"
                  />
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    <Skeleton
                      className="h-4 w-[min(140px,100%)]"
                      data-testid="sidebar-user-skeleton-name"
                    />
                    <Skeleton className="h-3 w-[100px]" />
                  </div>
                </>
              ) : (
                <>
                  <img
                    src={user.image ?? NoAvatarImage}
                    alt="Person"
                    width={32}
                    height={32}
                    className="rounded-full"
                    data-testid="sidebar-user-image"
                  />
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      <span
                        data-testid="sidebar-username"
                        className="max-w-[140px] truncate text-sm font-semibold"
                      >
                        {user.name}
                      </span>
                      {user.label && <Tag label={user.label} size="small" color="cyan" />}
                    </div>
                    {user.jobTitle?.trim() ? (
                      <span
                        className="text-xs text-custom-warm-grey-600"
                        data-testid="sidebar-jobtitle"
                      >
                        {user.jobTitle}
                      </span>
                    ) : null}
                  </div>
                </>
              )}
            </div>
            <Dropdown open={isOpenDropdownMenu} onOpenChange={setIsOpenDropdownMenu}>
              <DropdownMenuTrigger
                data-testid="sidebar-dropdown-trigger"
                variant="transparent"
                asChild
              >
                {open ? (
                  <IconButton
                    variant="teritary"
                    icon={<MoreVerticalIcon height={20} width={20} />}
                    size="sm"
                    className="ml-auto text-custom-warm-grey-400 px-0"
                    aria-label="Expand user actions"
                  />
                ) : isUserProfileLoading ? (
                  <Skeleton className="size-8 shrink-0 cursor-pointer rounded-full" />
                ) : (
                  <img
                    src={user.image ?? NoAvatarImage}
                    alt="Person"
                    width={32}
                    height={32}
                    className="rounded-full cursor-pointer !p-0"
                  />
                )}
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-[220px] gap-2">
                <div className="flex items-center px-3 py-2 gap-2.5">
                  <span className="text-sm text-custom-warm-grey-700 font-medium">
                    {t('dashboardSidebar.role')}
                  </span>
                  {user.label && <Tag label={user.label} size="small" color="cyan" />}
                </div>
                <DropdownMenuSeparator className="my-0" />
                <div className="space-y-[2px]">
                  <DropdownMenuSimpleItem
                    className="text-sm text-custom-warm-grey-700"
                    onClick={handleNavigateAccountSettings}
                  >
                    <UserIcon className="text-custom-warm-grey-400" />{' '}
                    {t('dashboardSidebar.profileSettings')}
                  </DropdownMenuSimpleItem>
                  <DropdownMenuSimpleItem
                    onClick={handleLogout}
                    className="text-sm text-custom-warm-grey-700"
                  >
                    <LogOutIcon width={20} height={20} className="text-custom-warm-grey-400" />{' '}
                    {t('dashboardSidebar.signOut')}
                  </DropdownMenuSimpleItem>
                </div>
              </DropdownMenuContent>
            </Dropdown>
          </div>
          <div className="flex gap-1 items-center group-data-[collapsible=icon]:hidden">
            <span className="text-xs text-custom-warm-grey-400 font-medium">
              {t('dashboardSidebar.powered')}
            </span>
            <img src={ChirpeeImage} alt="Chirpee" width={55} height={16.16} />
          </div>
        </SidebarFooter>
      </Sidebar>
    </SlidebarWrapper>
  );
};

export const DashboardSidebar = ({
  groups,
  user,
  header,
  onLogout,
  sidebarProps,
  isNavigationLoading,
  isUserProfileLoading,
}: DashboardSidebarProps) => {
  return (
    <DashboardSidebarContent
      user={user}
      groups={groups}
      header={header}
      onLogout={onLogout}
      sidebarProps={sidebarProps}
      isNavigationLoading={isNavigationLoading}
      isUserProfileLoading={isUserProfileLoading}
    />
  );
};
